import { ReactElement, useCallback, useMemo } from 'react';
import DeleteModelDialog from '@casops/dialogs/delete-model-dialog/DeleteModelDialog';
import { ModelName } from '@casops/store/api';
import { ModelWithId } from '@casops/store/apis/commonModels';
import { useTranslation } from 'react-i18next';
import { ModelDeleteProps } from '../../ModelActionsCellRenderer';

interface DeleteActionProps<TData extends ModelWithId, TQueryArg = ModelWithId>
  extends ModelDeleteProps<TData, TQueryArg> {
  data: TData;
  modelName: ModelName;
  isIconButton?: boolean;
  navigateTo?: string;
}

export const DeleteAction = <TData extends ModelWithId, TQueryArg = ModelWithId>({
  data,
  modelName,
  canDeleteItem,
  getItemName,
  getQueryArg,
  useDeleteModelMutation,
  isIconButton,
  navigateTo,
}: DeleteActionProps<TData, TQueryArg>): ReactElement => {
  const { t } = useTranslation();

  const isDeleteItemAllowed = useMemo(() => {
    if (data) {
      return typeof canDeleteItem === 'function' ? canDeleteItem(data) : canDeleteItem;
    }
    return false;
  }, [data, canDeleteItem]);

  const itemName = data ? getItemName(data) : t('Unknown');

  const [deleteModel, { error, isLoading }] = useDeleteModelMutation();

  // Create an async callback that populates the patch mutation trigger with arguments from the row data.
  const deleteModelCallback = useCallback(async (): Promise<void> => {
    return await deleteModel(getQueryArg(data)).unwrap();
  }, [deleteModel, data, getQueryArg]);

  return (
    <DeleteModelDialog
      itemName={itemName}
      modelName={modelName}
      disabled={!isDeleteItemAllowed}
      deleteModelCallback={deleteModelCallback}
      error={error}
      isLoading={isLoading}
      isIconButton={isIconButton}
      navigateTo={navigateTo}
    />
  );
};

export default DeleteAction;
